import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { TrendingUp, TrendingDown, Clock, Target, CheckCircle } from "lucide-react";
import { AccuracyTrendChart } from "./AccuracyTrendChart";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

interface TopicAnalyticsOverviewProps {
  data: any;
}

export function TopicAnalyticsOverview({ data }: TopicAnalyticsOverviewProps) {
  const { overview, accuracyTrend, difficultyBreakdown, tagPerformance, performanceTrend } = data;

  const difficultyLabels: Record<number, string> = {
    1: 'Very Easy',
    2: 'Easy',
    3: 'Medium',
    4: 'Hard',
    5: 'Very Hard',
  };

  return (
    <div className="space-y-6">
      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Overall Accuracy</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overview.accuracy}%</div>
            <div className="flex items-center text-xs text-muted-foreground mt-1">
              {performanceTrend.improving ? (
                <TrendingUp className="h-3 w-3 text-green-500 mr-1" />
              ) : (
                <TrendingDown className="h-3 w-3 text-red-500 mr-1" />
              )}
              <span className={performanceTrend.improving ? "text-green-500" : "text-red-500"}>
                {performanceTrend.improving ? '+' : ''}{performanceTrend.trendPercentage}%
              </span>
              <span className="ml-1">vs last period</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Questions Answered</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {overview.questionsAttempted}/{overview.totalQuestions}
            </div>
            <Progress value={overview.completionRate} className="mt-2" />
            <p className="text-xs text-muted-foreground mt-1">
              {overview.completionRate}% completion
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Time Spent</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {overview.timeSpent < 60 
                ? `${overview.timeSpent}m` 
                : `${Math.floor(overview.timeSpent / 60)}h ${overview.timeSpent % 60}m`
              }
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {overview.totalAttempts} total attempts
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Time/Question</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {Math.round((overview.timeSpent * 60) / overview.totalAttempts)}s
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Target: 60-75s
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Accuracy Trend */}
        {accuracyTrend && accuracyTrend.length > 0 && (
          <AccuracyTrendChart data={accuracyTrend} />
        )}

        {/* Difficulty Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Performance by Difficulty</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={difficultyBreakdown}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="difficulty" 
                  className="text-xs"
                  tickFormatter={(value) => difficultyLabels[value] || `Level ${value}`}
                />
                <YAxis className="text-xs" domain={[0, 100]} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--background))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                  formatter={(value: number) => [`${value}%`, 'Accuracy']}
                  labelFormatter={(value) => difficultyLabels[value] || `Level ${value}`}
                />
                <Bar 
                  dataKey="accuracy" 
                  fill="hsl(var(--primary))" 
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Tag Performance */}
      {tagPerformance && tagPerformance.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Performance by Topic Area</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {tagPerformance.slice(0, 8).map((tag: any) => (
                <div key={tag.tag} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{tag.tag}</span>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        tag.accuracy >= 85 ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400' :
                        tag.accuracy >= 70 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400' :
                        'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                      }`}>
                        {tag.accuracy >= 85 ? 'Strong' : tag.accuracy >= 70 ? 'Good' : 'Weak'}
                      </span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-muted-foreground">
                        {tag.attempts} attempts
                      </span>
                      <span className="font-semibold">{tag.accuracy}%</span>
                    </div>
                  </div>
                  <Progress value={tag.accuracy} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
